import { useCallback, useEffect, useState } from 'react';
import { useNotify, useRecordContext } from 'react-admin';
import {
  DrugTestReport,
  DrugTestReportsListResponse,
  DrugTestStatusHistoryItem,
  DrugTestWebhookEventItem,
} from './types';

const API_URL = import.meta.env.VITE_API_URL;

const getAuthHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem('access_token')}`,
  'Content-Type': 'application/json',
  Accept: 'application/json',
});

const useController = () => {
  const record = useRecordContext();
  const notify = useNotify();

  const [reports, setReports] = useState<DrugTestReport[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [history, setHistory] = useState<DrugTestStatusHistoryItem[]>([]);
  const [webhookEvents, setWebhookEvents] = useState<DrugTestWebhookEventItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [secondaryLoading, setSecondaryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const workerId = record?.id;

  const selectedReport = reports.find((r) => r.id === selectedReportId) ?? null;

  const fetchReports = useCallback(async () => {
    if (!workerId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/admin/candidates/${workerId}/drug_test_reports`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch drug test reports');
      const data = (await response.json()) as DrugTestReportsListResponse;
      const nextReports = data.reports ?? [];
      setReports(nextReports);
      setSelectedReportId((current) => {
        if (current && nextReports.some((r) => r.id === current)) return current;
        return nextReports[0]?.id ?? null;
      });
    } catch (err) {
      console.error('Error fetching drug test reports:', err);
      setError('Error fetching drug test reports');
      setReports([]);
      setSelectedReportId(null);
      notify('Error fetching drug test reports', { type: 'warning' });
    } finally {
      setLoading(false);
    }
  }, [workerId, notify]);

  const fetchSecondary = useCallback(
    async (reportId: string) => {
      setSecondaryLoading(true);
      try {
        const headers = getAuthHeaders();
        const [historyRes, webhooksRes] = await Promise.all([
          fetch(`${API_URL}/admin/drug_test_reports/${reportId}/history`, { headers }),
          fetch(`${API_URL}/admin/drug_test_reports/${reportId}/webhook_events`, { headers }),
        ]);
        if (!historyRes.ok) throw new Error('Failed to fetch drug test history');
        if (!webhooksRes.ok) throw new Error('Failed to fetch drug test webhook events');

        const historyData = (await historyRes.json()) as DrugTestStatusHistoryItem[];
        const webhooksData = (await webhooksRes.json()) as DrugTestWebhookEventItem[];
        setHistory(historyData);
        setWebhookEvents(webhooksData);
      } catch (err) {
        console.error('Error fetching drug test report details:', err);
        setHistory([]);
        setWebhookEvents([]);
        notify('Error fetching drug test report details', { type: 'warning' });
      } finally {
        setSecondaryLoading(false);
      }
    },
    [notify]
  );

  const selectReport = useCallback((reportId: string) => {
    setSelectedReportId(reportId);
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    if (!selectedReportId) {
      setHistory([]);
      setWebhookEvents([]);
      return;
    }
    fetchSecondary(selectedReportId);
  }, [selectedReportId, fetchSecondary]);

  return {
    reports,
    selectedReport,
    selectedReportId,
    selectReport,
    history,
    webhookEvents,
    loading,
    secondaryLoading,
    error,
    refetch: fetchReports,
  };
};

export default useController;
